// src/components/guardias/TurnoInfoDialog.tsx
import { useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PeopleIcon from "@mui/icons-material/People";
import PublishIcon from "@mui/icons-material/Publish";
import BlockIcon from "@mui/icons-material/Block";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { cancelarTurno, publicarTurno, removeTurno } from "../../api/guardias";
import { useCentros } from "../../hooks/useCentros";
import { useAuth } from "../../context/AuthContext";

type Props = {
  open: boolean;
  onClose: () => void;
  turno: any | null; // Turno normalizado
  onOpenAsignaciones?: (turnoId: number, capacidad: number) => void;
  onChanged?: () => void;
};

const hhmm = (s?: string | null) => (s ? String(s).slice(0, 5) : "");

export default function TurnoInfoDialog({
  open,
  onClose,
  turno,
  onOpenAsignaciones,
  onChanged,
}: Props) {
  const [busy, setBusy] = useState(false);
  const { rows: centros = [] } = useCentros(); // catálogo de centros
  const { user } = useAuth();
  const isProfesor = !!user?.roles?.includes("profesor");

  // Hook antes de cualquier return condicional
  const fechaLabel = useMemo(
    () => (turno ? dayjs(turno.fecha).format("dddd D MMM YYYY") : ""),
    [turno]
  );

  // Si no hay turno, no renderizamos nada
  if (!turno) return null;

  const nombres =
    turno.asignados && turno.asignados.length
      ? turno.asignados
      : (turno.asignaciones ?? [])
          .filter((a: any) => a?.estado === "asignado")
          .map((a: any) => a?.usuario?.nombreCompleto || a?.usuario?.email)
          .filter(Boolean);

  const esPresencial = Number(turno.formatoId) === 2;

  // Nombre de centro resuelto por id (sin useMemo para evitar hooks condicionales)
  const centroNombre = (() => {
    if (!esPresencial) return "—";
    const id = Number(turno.centroId ?? (turno as any)?.centro_id);
    const hit = centros.find((c: any) => Number(c.id) === id);
    return hit?.nombre ?? "—";
  })();

  const onPublicar = async () => {
    try {
      setBusy(true);
      await publicarTurno(turno.id);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const onCancelar = async () => {
    try {
      setBusy(true);
      await cancelarTurno(turno.id);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const onEliminar = async () => {
    if (!window.confirm("¿Eliminar este turno? Esta acción no se puede deshacer.")) return;
    try {
      setBusy(true);
      await removeTurno(turno.id);
      onChanged?.();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        Información de guardia
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
            {fechaLabel}
          </Typography>
          <Typography variant="h5">
            {hhmm(turno.horaInicio)}–{hhmm(turno.horaFin)}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label={`Estado: ${turno.estado}`}
              color={
                turno.estado === "publicado"
                  ? "success"
                  : turno.estado === "cancelado"
                    ? "warning"
                    : "default"
              }
            />
            <Chip label={`Formato: ${esPresencial ? "Presencial" : "Online"}`} />
            <Chip label={`Centro: ${esPresencial ? centroNombre : "—"}`} />
            {/* 🔕 Capacidad ocultada (no se muestra) */}
          </Stack>

          {turno.nota && (
            <>
              <Divider />
              <Typography variant="subtitle2">Nota</Typography>
              <Typography>{turno.nota}</Typography>
            </>
          )}

          <Divider />

          <Stack direction="row" alignItems="center" spacing={1}>
            <PeopleIcon fontSize="small" />
            <Typography variant="subtitle2">Asignados</Typography>
          </Stack>

          <List dense>
            {(nombres.length ? nombres : ["(Sin asignar)"]).map((n: string, idx: number) => (
              <ListItem key={idx} disableGutters>
                <ListItemText primary={n} />
              </ListItem>
            ))}
          </List>
        </Stack>
      </DialogContent>

      {/* Acciones — ocultas para PROFESOR */}
      {!isProfesor && (
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Box>
            <Button
              variant="outlined"
              startIcon={<PeopleIcon />}
              onClick={() => onOpenAsignaciones?.(turno.id, turno.capacidad)}
            >
              ASIGNACIONES
            </Button>
          </Box>

          <Stack direction="row" spacing={1}>
            {turno.estado !== "publicado" && (
              <Tooltip title="Publicar turno">
                <span>
                  <Button disabled={busy} onClick={onPublicar} startIcon={<PublishIcon />}>
                    PUBLICAR
                  </Button>
                </span>
              </Tooltip>
            )}
            {turno.estado !== "cancelado" && (
              <Tooltip title="Cancelar turno">
                <span>
                  <Button
                    disabled={busy}
                    color="warning"
                    onClick={onCancelar}
                    startIcon={<BlockIcon />}
                  >
                    CANCELAR
                  </Button>
                </span>
              </Tooltip>
            )}
            {turno.estado === "borrador" && (
              <Tooltip title="Eliminar turno">
                <span>
                  <Button
                    disabled={busy}
                    color="error"
                    onClick={onEliminar}
                    startIcon={<DeleteOutlineIcon />}
                  >
                    ELIMINAR
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        </DialogActions>
      )}
    </Dialog>
  );
}
